import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";
import { ClientReferral } from "@/lib/schema";
import clientReferralIcon from "@/assets/dashboard/sidebar/client_referral.svg";

interface ClientReferralsListProps {
  referrals: ClientReferral[];
  onReferClient: () => void;
  isLoading?: boolean;
  className?: string;
}

export function ClientReferralsList({ referrals, onReferClient, isLoading = false, className = "" }: ClientReferralsListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5; // Show only 5 referrals per page

  // Calculate pagination
  const totalPages = Math.ceil(referrals.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentReferrals = referrals.slice(startIndex, endIndex);

  // Reset to page 1 when referrals change
  useEffect(() => {
    setCurrentPage(1);
  }, [referrals.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
      contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
      meeting_scheduled: { label: 'Meeting Scheduled', color: 'bg-purple-100 text-purple-800' },
      converted: { label: 'Converted', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Not Suitable', color: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Loading skeleton components
  const MobileSkeletonCard = () => (
    <div className="p-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="flex-shrink-0 ml-3">
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  );

  const DesktopSkeletonRow = () => (
    <div className="grid grid-cols-5 gap-4 p-4">
      <div className="flex items-center">
        <div className="min-w-0 space-y-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex items-center">
        <div className="min-w-0 space-y-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-36" />
        </div>
      </div>
      <div className="flex items-center">
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <div className="flex items-center">
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="flex items-center">
        <Skeleton className="h-4 w-16" />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Card className={`border border-gray-200 bg-white ${className}`}>
        <CardContent className="p-0">
          {/* Mobile Loading Skeleton */}
          <div className="lg:hidden divide-y divide-slate-200">
            {Array.from({ length: pageSize }).map((_, index) => (
              <MobileSkeletonCard key={index} />
            ))}
          </div>

          {/* Desktop Loading Skeleton */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Desktop Table Header */}
              <div className="grid grid-cols-5 py-3 px-4 gap-2 bg-[#F6F6F6] border border-[#EAEAEA] text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal">
                <div>COMPANY</div>
                <div>CONTACT</div>
                <div>STATUS</div>
                <div>DATE REFERRED</div>
                <div>REWARD</div>
              </div>

              {/* Desktop Table Body Skeleton */}
              <div className="divide-y divide-gray-200">
                {Array.from({ length: pageSize }).map((_, index) => (
                  <DesktopSkeletonRow key={index} />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border border-gray-200 bg-white ${className}`}>
      <CardContent className="p-0">
        {referrals.length == 0 ? (
          <div className="text-center py-8">
            <img 
              src={clientReferralIcon} 
              alt="Client Referral" 
              className="h-12 w-12 mx-auto mb-3" 
              style={{
                filter: "brightness(0) saturate(100%) invert(75%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(75%) contrast(75%)"
              }}
            />
            <h3 className="text-fluid-base font-semibold text-slate-900 mb-2">No client referrals yet</h3>
            <p className="text-fluid-sm text-slate-500 space-grotesk mb-4">
              Start referring companies to earn rewards!
            </p>
            <Button 
              onClick={onReferClient}
              variant="primary"
              size="sm"
            >
              <img 
                src={clientReferralIcon} 
                alt="Client Referral" 
                className="h-4 w-4 mr-2"
              />
              Refer Your First Company
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="lg:hidden divide-y divide-slate-200">
              {currentReferrals.map((referral) => (
                <div key={referral.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="space-y-3">
                    {/* Company & Contact Row */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-fluid-sm font-medium text-slate-900 space-grotesk truncate">{referral.companyName}</h4>
                        <p className="text-fluid-sm text-slate-600 space-grotesk truncate">{referral.pointOfContact}</p>
                        <p className="text-xs text-slate-500 space-grotesk">{referral.email}</p>
                      </div>
                      <div className="flex-shrink-0 ml-3">
                        <span 
                          className="space-grotesk font-bold text-fluid-lg"
                          style={{
                            lineHeight: '140%',
                            letterSpacing: '0%',
                            color: '#2D2D2D'
                          }}
                        >
                          R{formatRewardAmount(referral.rewardAmount)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Status & Date Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusBadge(referral.status)}
                      </div>
                      <span className="text-fluid-sm text-slate-500 space-grotesk">{formatDateForSouthAfrica(referral.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Desktop Table Header */}
                <div className="grid grid-cols-5 py-3 px-4 gap-2 bg-[#F6F6F6] border border-[#EAEAEA] text-[10px] font-semibold text-gray-500 uppercase leading-[140%] tracking-normal">
                  <div>COMPANY</div>
                  <div>CONTACT</div>
                  <div>STATUS</div>
                  <div>DATE REFERRED</div>
                  <div>REWARD</div>
                </div>

                {/* Desktop Table Body */}
                <div className="divide-y divide-gray-200">
                  {currentReferrals.map((referral) => (
                    <div key={referral.id} className="grid grid-cols-5 gap-4 p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 text-sm leading-[140%] tracking-normal block truncate">{referral.companyName}</span>
                          <span className="text-gray-500 text-xs leading-[140%] tracking-normal truncate block">{referral.website || 'No website'}</span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="min-w-0">
                          <span className="text-gray-900 font-medium text-sm leading-[140%] tracking-normal block truncate">{referral.pointOfContact}</span>
                          <span className="text-gray-500 text-xs leading-[140%] tracking-normal truncate block">{referral.email}</span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        {getStatusBadge(referral.status)}
                      </div>

                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal">{formatDateForSouthAfrica(referral.createdAt)}</span>
                      </div>

                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal">R{referral.rewardAmount.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Pagination */}
        {referrals.length > pageSize && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </CardContent>
    </Card>
  );
}
